import React from 'react';
import { Typography, Autocomplete, TextButton } from 'seagull';
import { matchSorter } from 'match-sorter';
import compact from 'lodash/compact';
import { Plus } from 'seagull/icons';
import { assertUnreachable } from "../../infra/utils";
import { useStore } from "../../store/store-context";
import { ContactModel } from "../../models/ContactModel";
const staticOptions = {
  emptyState: {
    type: 'emptyState'
  },
  initial: {
    type: 'initial'
  },
  newContact: {
    type: 'newContact'
  },
  sampleContact: {
    type: 'sampleContact'
  }
};
const noOptionsText = 'No matches found. Try again or create a new contact.';
export const contactPickerTestId = 'agreement-studio-contact-picker';
export default function ContactPicker(props) {
  const {
    value,
    error,
    helperText,
    label = 'Contact',
    disabled,
    onChange: _onChange,
    hideSampleContact = false
  } = props;
  const contacts = useStore(state => state.contacts.data) || [];
  const existingContactsOptions = contacts.filter(c => !c.isSample() && c.isActive()).map(c => ({
    type: 'existingContact',
    contact: c.toJSON()
  }));
  const options = [staticOptions.initial, staticOptions.emptyState, staticOptions.newContact, staticOptions.sampleContact, ...existingContactsOptions];
  let currentOption;
  if (value.isSample()) {
    currentOption = {
      type: 'sampleContact'
    };
  } else if (value instanceof ContactModel) {
    currentOption = {
      type: 'existingContact',
      contact: value.toJSON()
    };
  } else if (value.hasPartialContactDetails()) {
    currentOption = staticOptions.newContact;
  } else {
    currentOption = staticOptions.initial;
  }
  const showError = currentOption.type === 'initial' && error;
  return /*#__PURE__*/React.createElement(Autocomplete, {
    textFieldProps: {
      label,
      placeholder: 'Type a company name, email or name',
      helperText: showError && helperText,
      error: showError
    },
    options: options,
    disabled: disabled,
    value: currentOption,
    onChange: (_, newValue) => {
      if (!newValue || typeof newValue === 'string' || Array.isArray(newValue)) {
        _onChange({
          type: 'initial'
        });
        return;
      }
      switch (newValue.type) {
        case 'newContact':
          _onChange({
            type: 'newContact'
          });
          break;
        case 'existingContact':
          _onChange({
            type: 'existingContact',
            id: newValue.contact.id
          });
          break;
        case 'sampleContact':
          _onChange({
            type: 'sampleContact'
          });
          break;
        case 'emptyState':
          break;
        case 'initial':
          _onChange({
            type: 'initial'
          });
          break;
        default:
          assertUnreachable(newValue);
      }
    },
    renderOption: option => {
      switch (option.type) {
        case 'existingContact':
          return /*#__PURE__*/React.createElement(Typography, {
            as: "p"
          }, option.contact.companyName, /*#__PURE__*/React.createElement(Typography, {
            as: "div",
            skin: "secondary"
          }, option.contact.firstName, " ", option.contact.lastName, " (", option.contact.email, ")"));
        case 'emptyState':
          return /*#__PURE__*/React.createElement(Typography, {
            "data-testid": "contact-picker-empty"
          }, noOptionsText);
        case 'newContact':
          return /*#__PURE__*/React.createElement(TextButton, {
            "data-testid": "contact-picker-add-contact",
            underline: "none",
            prefixIcon: /*#__PURE__*/React.createElement(Plus, null)
          }, "New contact");
        case 'sampleContact':
          return /*#__PURE__*/React.createElement(TextButton, {
            "data-testid": "contact-picker-sample-contact",
            underline: "none"
          }, "Sample contact", ' ', /*#__PURE__*/React.createElement(Typography, {
            skin: "secondary",
            as: "span"
          }, "(Send yourself a test agreement)"));
        default:
          return null;
      }
    },
    getOptionSelected: (option, value) => {
      if (option.type === 'existingContact' && value.type === 'existingContact') {
        return option.contact.id === value.contact.id;
      } else {
        return option.type === value.type;
      }
    },
    getOptionLabel: option => {
      switch (option.type) {
        case 'existingContact':
          return "".concat(option.contact.companyName, ", ").concat(option.contact.firstName, " ").concat(option.contact.lastName, " (").concat(option.contact.email, ")");
        case 'emptyState':
          return noOptionsText;
        case 'newContact':
          return 'New contact';
        case 'sampleContact':
          return 'Sample contact (Send yourself a test agreement)';
        default:
          return '';
      }
    },
    filterOptions: (options, _ref) => {
      let {
        inputValue
      } = _ref;
      const baseOptions = hideSampleContact ? [staticOptions.newContact] : [staticOptions.newContact, staticOptions.sampleContact];
      if (contacts.length === 0) {
        return compact([inputValue && staticOptions.emptyState, ...baseOptions]);
      }
      const filteredContacts = matchSorter(options, inputValue, {
        keys: ['contact.companyName', 'contact.firstName', 'contact.lastName', 'contact.email']
      });
      return filteredContacts.length > 0 ? [...baseOptions, ...filteredContacts] : [staticOptions.emptyState, ...baseOptions];
    },
    noOptionsText: noOptionsText,
    getOptionDisabled: option => option.type === 'emptyState',
    selectOnFocus: true,
    "data-testid": contactPickerTestId
  });
}